import { RecordValueDetailsPanel } from "#batteries-included-components/Panels/DetailsPanel/RecordValueDetailsPanel";
import { RecordValueSourcesTablePanel } from "#batteries-included-components/Panels/TablePanels/RecordValueSourcesTablePanel";
import { useParams } from "#routers/hooks";
import BulkRecordValueStatusChangeButton from "#src/batteries-included-components/Buttons/BulkRecordValueStatusChangeButton/BulkRecordValueStatusChangeButton";
import { RecordValueSummaryPanel } from "#src/batteries-included-components/Panels/SummaryPanels/RecordValueSummaryPanel/RecordValueSummaryPanel";
import { RecordValueActivityLogTablePanel } from "#src/batteries-included-components/Panels/TablePanels/RecordValueActivityLogTablePanel";
import { useActionDetails } from "#src/components/hooks/useActionDetails";
import {
  EquipmentContext,
  EquipmentProvider,
} from "#src/contexts/EquipmentContext";
import { RecordContext, RecordProvider } from "#src/contexts/RecordContext";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { useBreadcrumbsFromRoute } from "#src/utils/route";
import { useQuery } from "@tanstack/react-query";
import { Page, Pill, PillProps } from "@validereinc/common-components";
import {
  AssetType,
  MeasurementsDomain,
  RecordValueStatusType,
  Resources,
} from "@validereinc/domain";
import { yearMonthName } from "@validereinc/utilities";
import React, { useContext } from "react";
import { EquipmentRecordValueDetailRoutePath } from "./index";

const EquipmentRecordValueDetailPageContent = () => {
  const params =
    useParams<
      Exclude<typeof EquipmentRecordValueDetailRoutePath.pathParams, undefined>
    >();

  const { equipment } = useContext(EquipmentContext) ?? {};
  const { record, refetch } = useContext(RecordContext) ?? {};

  const { data: measurementTypes } = useQuery({
    queryKey: ["measurementTypes"],
    queryFn: MeasurementsDomain.listMeasurementTypes,
  });
  const measurementType = measurementTypes?.find(
    ({ id }) => id === params.measurementType
  );

  const pageTitle = record
    ? `${yearMonthName(record.year_month)} ${measurementType?.name}`
    : EquipmentRecordValueDetailRoutePath.title;

  const [breadcrumbs] = useBreadcrumbsFromRoute(
    EquipmentRecordValueDetailRoutePath,
    {
      "/:equipmentId/detail": {
        title: equipment?.name,
      },
      "/:recordId": {
        title: record
          ? `${yearMonthName(record.year_month)} Record`
          : undefined,
      },
      "/:measurementType": {
        title: pageTitle,
      },
    }
  );

  const recordValue = record?.values.find(
    ({ measurement_type }) => measurement_type === measurementType?.id
  );
  const [filterConfigStorageKey, tableConfigStorageKey, tableStateStorageKey] =
    useStorageKey(
      ["filter-configuration", "table-configuration", "table-state"],
      `activity-logs-${Resources.RECORD_VALUE}-${recordValue?.id}`
    );

  const editInfo = useActionDetails({
    type: "edit",
    date: recordValue?.updated_at,
    byUserId: recordValue?.updated_by,
  });

  const recordStatusOptions: Record<
    RecordValueStatusType,
    { label: string; variant: PillProps["variant"] }
  > = {
    locked: { label: "Locked", variant: "default" },
    unlocked: { label: "Unlocked", variant: "info" },
  };

  return (
    <Page
      category={EquipmentRecordValueDetailRoutePath.previous?.title}
      breadcrumbs={breadcrumbs}
      actionRow={
        <BulkRecordValueStatusChangeButton
          recordId={record?.id ?? ""}
          measurementTypes={[recordValue?.measurement_type ?? ""]}
          onSuccess={() => {
            refetch?.();
          }}
          variant="primary"
          label="Change Record Value Status"
        />
      }
      title={
        !recordValue ? (
          pageTitle
        ) : (
          <>
            {pageTitle}&nbsp;
            <Pill
              variant={
                recordValue?.status
                  ? recordStatusOptions[recordValue.status].variant
                  : "default"
              }
              hasDot={false}
            >
              {recordValue?.status
                ? recordStatusOptions[recordValue.status].label
                : ""}
            </Pill>
          </>
        )
      }
      renderMeta={
        !editInfo.description
          ? undefined
          : ({ MetaSegments }) => (
              <MetaSegments values={[editInfo.description]} />
            )
      }
    >
      <RecordValueSummaryPanel />
      <RecordValueDetailsPanel />
      <RecordValueSourcesTablePanel type={AssetType.EQUIPMENT} />
      <RecordValueActivityLogTablePanel
        recordId={record?.id ?? ""}
        measurement_type={recordValue?.measurement_type}
        filterConfigStorageKey={filterConfigStorageKey}
        tableConfigStorageKey={tableConfigStorageKey}
        tableStateStorageKey={tableStateStorageKey}
      />
    </Page>
  );
};

export const EquipmentRecordValueDetailPage = () => (
  <EquipmentProvider>
    <RecordProvider>
      <EquipmentRecordValueDetailPageContent />
    </RecordProvider>
  </EquipmentProvider>
);
